import * as React from "react";
import graphql from "babel-plugin-relay/macro";
import { useMutation } from "react-relay";
import Button from "./Buttons/Button";
import DatePicker from "./Inputs/Date";
import Modal from "./Modal";
import styled from "styled-components";

export interface SelectedInspection {
  id: string;
  externalId: number;
  year: number;
}

function PlanModal(props: {
  show?: boolean;
  selectedInspections?: SelectedInspection[];
  date?: string;
  showDelete?: boolean;
  onDismiss?: () => void;
  onSuccess?: () => void;
}) {
  const minDate = React.useMemo(() => {
    if (!props.selectedInspections || props.selectedInspections.length === 0) {
      return new Date();
    } else {
      const years = props.selectedInspections.map(
        (selectedInspection) => selectedInspection.year
      );
      const highestYear = Math.max(...years);
      return new Date(highestYear, 0, 1);
    }
  }, [props.selectedInspections]);

  const [date, setDate] = React.useState<string | undefined>(getInitialDate());
  const [error, setError] = React.useState<string | undefined>();
  const [commitDate, loading] = useMutation(
    graphql`
      mutation PlanModalMutation(
        $id: ID!
        $state: InspectionfieldStateEnum!
        $timestamp: Float!
      ) {
        modifyInspectionfieldDate(
          input: { id: $id, timestamp: $timestamp, state: $state }
        ) {
          inspectionfield {
            state
            plannedDate
          }
        }
      }
    `
  );

  function getInitialDate() {
    if (props.date) {
      return props.date;
    } else {
      const date = new Date() > minDate ? new Date() : minDate;
      return date.toISOString().split("T")[0];
    }
  }

  const handleChangeDate = (date?: string) => {
    if (!date) return;
    setDate(date);
  };

  const handleSubmitDate = () => {
    if (loading) return;
    if (!date) {
      setError("Datum is verplicht");
      return;
    }
    props.selectedInspections?.forEach((selectedInspection) => {
      commitDate({
        variables: {
          id: selectedInspection.id,
          timestamp: new Date(date).getTime() / 1000,
          state: date ? "PLANNED" : "ASSIGNED",
        },
      });
    });
    props.onDismiss && props.onDismiss();
    props.onSuccess && props.onSuccess();
  };
  const handleRemoveDate = () => {
    if (loading || !props.showDelete) return;
    props.selectedInspections?.forEach((selectedInspection) => {
      commitDate({
        variables: {
          id: selectedInspection.id,
          timestamp: Date.now() / 1000,
          state: "ASSIGNED",
        },
      });
    });
    props.onDismiss && props.onDismiss();
    props.onSuccess && props.onSuccess();
  };
  return (
    <Modal show={Boolean(props.show)} onDismiss={props.onDismiss}>
      <Modal.Header>Schouw inplannen</Modal.Header>
      <Modal.Body
        style={{ display: "flex", flexDirection: "row", width: "450px" }}
      >
        <div style={{ width: "60%" }}>
          <BodyText>Selecteer een datum</BodyText>
          <DatePicker
            date={date}
            onChange={handleChangeDate}
            minDate={minDate.toDateString()}
          />
          {error && <ErrorText>{error}</ErrorText>}
        </div>
        {props.selectedInspections && props.selectedInspections.length > 0 && (
          <div style={{ width: "40%" }}>
            <BodyText>
              Geselecteerde
              {props.selectedInspections && props.selectedInspections.length > 1
                ? " BE's"
                : " BE"}
              :
            </BodyText>
            <SelectedIdsBorder>
              <SelectedIdsContainer>
                {props.selectedInspections?.map((selectedInspection) => (
                  <div
                    key={`${selectedInspection.externalId}-${selectedInspection.year}`}
                  >
                    BE: {selectedInspection.externalId}
                  </div>
                ))}
              </SelectedIdsContainer>
            </SelectedIdsBorder>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          color="green"
          size="medium"
          label="Inplannen"
          disabled={loading}
          onClick={handleSubmitDate}
        />
        {props.showDelete ? (
          <Button
            bordered
            size="medium"
            color="white"
            label="Datum leegmaken"
            disabled={loading}
            onClick={handleRemoveDate}
          />
        ) : null}
      </Modal.Footer>
    </Modal>
  );
}
const BodyText = styled.div`
  /* Text small */
  font-family: "Roboto";
  font-style: normal;
  font-weight: 700;
  font-size: 13px;
  line-height: 120%;

  /* Donkergrijs */
  color: #212529;
  padding-top: 5px;
  padding-bottom: 5px;
`;

const ErrorText = styled.div`
  color: #f92b2b;
  font-family: "Roboto";
  font-style: normal;
  font-weight: 700;
  font-size: 13px;
  line-height: 120%;

  /* or 19px */
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-top: 5px;
  padding-bottom: 5px;
`;

const SelectedIdsBorder = styled.div`
  color: black;
  border: solid 1px rgb(229, 229, 229);
  border-radius: 5px;
`;

const SelectedIdsContainer = styled.div`
  margin: 8px;
  max-height: 150px;
  overflow: auto;
`;

export { PlanModal };
export default PlanModal;
